import React, {
    useCallback,
    useEffect,
    useState,
    useMemo,
    useRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useCookies } from "react-cookie";
import { nanoid } from "@reduxjs/toolkit";
import { StyledPollItemWrapper, ClosePollContainer } from "./styled";
import { IChannel, RootStateType } from "@Interfaces";
import { IChoice, IPoll, PollType } from "@Interfaces/Entities/Poll";
import PollItemHeader from "./PollItemHeader/PollItemHeader.component";
import PollItemFooter from "./PollItemFooter/PollItemFooter";
import PollItemContent from "./PollItemContent/PollItemContent.component";
import Graph from "@Components/PollItem/Graph/Graph.component";
import Choice from "@Components/PollItem/Choice/Choice.component";
import { NpsVote } from "@Components/Nps";
import {
    StyledGraphWrapper,
    StyledGraph,
} from "@Components/PollItem/Graph/Graph.styled";
import PollTimer from "./PollTimer";
import { CalcExpiryDate } from "@Utils/calcExpiryDate";
import AccessTime from "@mui/icons-material/AccessTime";
import { Typography } from "@mui/material";
import { PollActions } from "@Store";
import { useTranslation } from "react-i18next";
import { DraggableProvidedDragHandleProps } from "react-beautiful-dnd";
import { readGuestVotes, getPollIdsFromCookies } from "@Utils/guestVotes";
import { readDoubleVoteMeta } from "@Utils/doubleVoteMeta";
import useVoteGraceTimer from "@Hooks/useVoteGraceTimer";

type ChoiceWrappedProps = {
    choice: IChoice;
    index: number;
};

interface IPollItem extends IPoll {
    disabledShare?: boolean;
    channel?: Partial<IChannel>;
    dragHandleProps?: DraggableProvidedDragHandleProps;
    isDraggable?: boolean;
}

export const PollItem: React.FC<IPollItem> = ({
    _id,
    author,
    channel_id,
    choices,
    comments,
    disabledShare = false,
    isHideResult,
    likes,
    published,
    question,
    total_votes,
    user_choice,
    votingTimeLimit,
    channel,
    active,
    report,
    multi_voting,
    poll_type,
    order,
    privacy,
    settings,
    vote_id,
    private: pollPrivate,
    dragHandleProps,
    isDraggable = false,
}: IPollItem) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<any>();
    const [votes, setVotes] = useState(total_votes);
    const [currentChoices, setCurrentChoices] = useState(choices);
    const {
        countdownEndsAt: undoCountdownEndsAt,
        start: startUndoGraceTimer,
        cancel: cancelUndoGraceTimer,
    } = useVoteGraceTimer();
    // One pending vote per poll during the 5s grace window; prevents multiple network calls.
    const queuedVoteRef = useRef<{
        choiceId: string;
        deviceData: string;
    } | null>(null);
    const [sealedChoiceId, setSealedChoiceId] = useState<string | null>(null);

    // Restore sealed vote state so a post-refresh double vote cannot be treated as a first vote; clamp between 0-2 to mirror the backend limit.
    const [sealedVoteCount, setSealedVoteCount] = useState<number>(() => {
        const doubleVoteEnabled =
            settings?.allow_double_vote ??
            channel?.settings?.allow_double_vote ??
            false;

        if (!doubleVoteEnabled) {
            return 0;
        }

        const meta = readDoubleVoteMeta(_id);
        const count = typeof meta?.voteCount === "number" ? meta.voteCount : 0;
        return Math.min(2, Math.max(0, count));
    });

    const allowDoubleVote =
        settings?.allow_double_vote ??
        channel?.settings?.allow_double_vote ??
        false;
    const allowComments =
        settings?.allow_comments ?? channel?.settings?.allow_comments ?? true;
    const doubleVoteCooldownMinutes =
        settings?.double_vote_cooldown_minutes ??
        channel?.settings?.double_vote_cooldown_minutes ??
        0;
    const userId = useSelector((state: RootStateType) => state.auth.user.id);
    const currentChannelId = useSelector(
        (state: RootStateType) => state.channels.current
    );
    const resolvedChannelId = channel_id || currentChannelId;
    const isGuestViewer = userId === "";
    const [cookies] = useCookies(["tb"]);

    // Check if this specific poll was voted on in the current session
    const guestVoteForThisPoll = useMemo(() => {
        // Pull fresh guest votes so new NPS votes (stored in localStorage) are reflected immediately.
        const storedGuestVotes = readGuestVotes();
        return storedGuestVotes.find((votedPoll) => votedPoll.pollId === _id);
    }, [_id]);

    // Check if guest voted in cookies (legacy support)
    const hasGuestVoteInCookies = useMemo(() => {
        if (!isGuestViewer) return false;
        const cookiePolls = getPollIdsFromCookies(cookies);
        return cookiePolls.includes(_id);
    }, [isGuestViewer, _id, cookies]);

    // Guest has voted if vote exists in session OR in cookies
    const guestHasVoted =
        Boolean(guestVoteForThisPoll) || hasGuestVoteInCookies;

    const expiryDate = CalcExpiryDate(votingTimeLimit, published);
    const [leftTime, setLeftTime] = useState(
        expiryDate.valueOf() - new Date().valueOf()
    );
    const isExpiredDate =
        votingTimeLimit === 0 ? false : leftTime > 0 ? false : true;

    const applyGuestVoteLocally = (selectedChoiceId: string) => {
        // This is to reflect a guest vote in UI
        setVotes((currentTotalVotes) => currentTotalVotes + 1);
        setCurrentChoices((previousChoices) =>
            previousChoices.map((choice) =>
                choice._id === selectedChoiceId
                    ? { ...choice, votes: choice.votes + 1 }
                    : choice
            )
        );
    };

    const revertGuestVoteLocally = (selectedChoiceId: string) => {
        setVotes((currentTotalVotes) => Math.max(0, currentTotalVotes - 1));
        setCurrentChoices((previousChoices) =>
            previousChoices.map((choice) =>
                choice._id === selectedChoiceId
                    ? {
                          ...choice,
                          votes: Math.max(0, (choice.votes || 0) - 1),
                      }
                    : choice
            )
        );
    };

    const switchGuestVoteLocally = (
        prevChoiceId: string,
        nextChoiceId: string
    ) => {
        setCurrentChoices((previousChoices) =>
            previousChoices.map((choice) => {
                if (choice._id === prevChoiceId) {
                    return {
                        ...choice,
                        votes: Math.max(0, (choice.votes || 0) - 1),
                    };
                }
                if (choice._id === nextChoiceId) {
                    return { ...choice, votes: (choice.votes || 0) + 1 };
                }
                return choice;
            })
        );
    };

    const clearQueuedVote = useCallback(() => {
        // Cancel any pending send and drop the queued payload.
        cancelUndoGraceTimer();
        queuedVoteRef.current = null;
    }, [cancelUndoGraceTimer]);

    const handleSendQueuedVote = useCallback(() => {
        // Finalize the queued vote after the grace window elapses.
        const payload = queuedVoteRef.current;
        if (!payload) return;
        const choiceText =
            currentChoices.find((c) => c._id === payload.choiceId)?.txt || "";
        dispatch(
            PollActions.submitVote(_id, channel_id, choiceText, {
                choice_id: payload.choiceId,
                device: payload.deviceData,
            })
        );
        setSealedChoiceId(payload.choiceId);
        setSealedVoteCount((prev) => Math.min(2, Math.max(1, prev + 1)));
        clearQueuedVote();
    }, [channel_id, clearQueuedVote, currentChoices, dispatch, _id]);

    const handleQueueVote = useCallback(
        (choiceId: string, deviceData: string) => {
            // Replace any existing pending vote and restart the timer.
            queuedVoteRef.current = { choiceId, deviceData };
            startUndoGraceTimer(handleSendQueuedVote);
        },
        [handleSendQueuedVote, startUndoGraceTimer]
    );

    const startUndoCountdown = useCallback(() => {
        startUndoGraceTimer();
    }, [startUndoGraceTimer]);

    const cancelUndoCountdown = useCallback(() => {
        clearQueuedVote();
    }, [clearQueuedVote]);

    const canUndoVote = Boolean(undoCountdownEndsAt);

    const ChoiceWrapped: React.FC<ChoiceWrappedProps> = ({ choice, index }) => (
        <Choice
            key={`choice-${_id}-${index}`}
            choice={choice}
            isExpiredDate={isExpiredDate}
            totalVotes={votes}
            pollId={_id}
            hasUserVoted={hasUserVoted()}
            isHideResult={isHideResult}
            isGuest={isGuestViewer}
            userChoice={user_choice}
            isActive={active}
            allowDoubleVote={allowDoubleVote}
            doubleVoteCooldownMinutes={doubleVoteCooldownMinutes}
            onLocalGuestVote={applyGuestVoteLocally}
            onLocalGuestUnvote={revertGuestVoteLocally}
            onLocalGuestVoteChange={switchGuestVoteLocally}
            onUnvoteCountdownStart={startUndoCountdown}
            onUndoCountdownCancel={cancelUndoCountdown}
            onQueueVote={handleQueueVote}
            onCancelQueuedVote={clearQueuedVote}
            sealedChoiceId={sealedChoiceId}
            sealedVoteCount={sealedVoteCount}
            canUndoVote={canUndoVote}
            multiVoting={Boolean(multi_voting)}
        />
    );

    const hasUserVoted = (): boolean => {
        const hasRecordedChoice = Array.isArray(user_choice)
            ? user_choice.length > 0
            : user_choice !== "";

        // For guests: check both recorded choice AND guest votes in session storage
        // For authenticated users: check recorded choice
        // For multi-voting: ignore guest vote status (allow multiple votes)
        return hasRecordedChoice || (guestHasVoted && !Boolean(multi_voting));
    };

    const handleVote = useCallback(
        async (choiceId: string) => {
            try {
                // Use Redux action to submit vote
                const DeviceDetector = require("device-detector-js");
                const deviceData = JSON.stringify(
                    new DeviceDetector().parse(navigator.userAgent)
                );

                dispatch(
                    PollActions.submitVote(_id, channel_id, choiceId, {
                        choice_id: choiceId,
                        device: deviceData,
                    })
                );
            } catch (error) {
                console.error("Failed to submit vote:", error);
            }
        },
        [dispatch, _id, channel_id]
    );

    const setExpiredDate = () => {
        setLeftTime(0);
    };

    const currentPoll = useSelector(
        (state: RootStateType) => state.poll
    ) as IPoll | null;

    // Also listen for updates from channels (for when viewing polls in a channel)
    const pollsInChannel = useSelector((state: RootStateType) => {
        const channel = state.channels.channels.find(
            (ch) => ch._id === channel_id
        );
        return channel?.polls || [];
    });

    useEffect(() => {
        if (currentPoll?._id === _id) {
            setVotes(currentPoll.total_votes);
            setCurrentChoices(currentPoll.choices);
        }
    }, [currentPoll, _id]);

    // Also update from channel polls (important for NPS polls and real-time updates)
    useEffect(() => {
        const pollInChannel = pollsInChannel.find((p) => p._id === _id);
        if (pollInChannel) {
            setVotes(pollInChannel.total_votes);
            setCurrentChoices(pollInChannel.choices);
        }
    }, [pollsInChannel, _id]);

    useEffect(() => {
        setVotes(total_votes);
        setCurrentChoices(choices);
    }, [total_votes, choices]);

    useEffect(() => {
        return () => {
            clearQueuedVote();
        };
    }, [clearQueuedVote]);

    const pollForEdit: IPoll = {
        _id,
        author,
        channel_id,
        choices: currentChoices,
        comments,
        isHideResult,
        likes,
        privacy,
        published,
        question,
        total_votes: votes,
        user_choice,
        multi_voting,
        poll_type,
        votingTimeLimit,
        report,
        active,
        settings,
        vote_id,
        private: pollPrivate,
        order,
    };

    return (
        <StyledPollItemWrapper
            key={`poll-item-${_id}`}
            className="tb-shadow-box"
        >
            <PollItemHeader
                pollId={_id}
                channelId={resolvedChannelId}
                author={author}
                published={published}
                question={question}
                choices={currentChoices}
                isActive={active}
                report={report}
                totalVotes={votes}
                pollForEdit={pollForEdit}
                dragHandleProps={dragHandleProps}
                isDraggable={isDraggable}
                multiVoting={Boolean(multi_voting)}
                channelClosed={Boolean(channel?.closed)}
            />
            <PollItemContent>
                {(() => {
                    const hasChoiceImage = currentChoices.some(
                        (choice: IChoice) =>
                            choice.hasOwnProperty("img") && choice["img"]
                    );

                    if (multi_voting) {
                        // Multi-voting poll
                        return hasChoiceImage ? (
                            <Graph>
                                {currentChoices.map(
                                    (choice: IChoice, index: number) => (
                                        <ChoiceWrapped
                                            key={nanoid()}
                                            choice={choice}
                                            index={index}
                                        ></ChoiceWrapped>
                                    )
                                )}
                            </Graph>
                        ) : (
                            <StyledGraphWrapper>
                                <StyledGraph>
                                    {currentChoices.map((choice, index) => (
                                        <ChoiceWrapped
                                            key={nanoid()}
                                            choice={choice}
                                            index={index}
                                        ></ChoiceWrapped>
                                    ))}
                                </StyledGraph>
                            </StyledGraphWrapper>
                        );
                    }

                    if (poll_type === PollType.NPS) {
                        // NPS poll (0-10 scale)
                        return (
                            <NpsVote
                                poll={
                                    {
                                        ...pollForEdit,
                                        poll_type: PollType.NPS,
                                    } as any
                                }
                                hasUserVoted={hasUserVoted()}
                                isDisabled={!active || isExpiredDate}
                                isHideResults={isHideResult}
                                isAuthor={author._id === userId}
                                isGuest={isGuestViewer}
                                allowDoubleVote={allowDoubleVote}
                                doubleVoteCooldownMinutes={
                                    doubleVoteCooldownMinutes
                                }
                                canUndoVote={canUndoVote}
                                onVote={handleVote}
                                onCountdownStart={startUndoCountdown}
                                // Hoisted timer API so UI and submit share one timer
                                undoCountdownEndsAt={undoCountdownEndsAt}
                                startUndoGraceTimer={startUndoGraceTimer}
                                cancelUndoGraceTimer={cancelUndoGraceTimer}
                                isUndoGraceActive={Boolean(undoCountdownEndsAt)}
                            />
                        );
                    }

                    return hasChoiceImage ? (
                        <Graph>
                            {currentChoices.map(
                                (choice: IChoice, index: number) => (
                                    <ChoiceWrapped
                                        key={nanoid()}
                                        choice={choice}
                                        index={index}
                                    ></ChoiceWrapped>
                                )
                            )}
                        </Graph>
                    ) : (
                        <StyledGraphWrapper>
                            <StyledGraph>
                                {currentChoices.map((choice, index) => (
                                    <ChoiceWrapped
                                        key={nanoid()}
                                        choice={choice}
                                        index={index}
                                    ></ChoiceWrapped>
                                ))}
                            </StyledGraph>
                        </StyledGraphWrapper>
                    );
                })()}
            </PollItemContent>

            {!active && !isExpiredDate ? (
                <ClosePollContainer>
                    <Typography
                        variant="body2"
                        color={leftTime <= 0 ? "error" : "textSecondary"}
                        component="p"
                        style={{
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <AccessTime />
                        {t("poll_item.closed")}
                    </Typography>
                </ClosePollContainer>
            ) : (
                <PollTimer
                    votingTimeLimit={votingTimeLimit}
                    leftTime={leftTime}
                    setExpiredDate={setExpiredDate}
                />
            )}
            <PollItemFooter
                total_votes={votes}
                comments={comments}
                likes={likes}
                hasUserVoted={hasUserVoted()}
                pollId={_id}
                authorId={author._id}
                channelId={resolvedChannelId}
                disabledShare={disabledShare}
                channel={channel}
                undoCountdownEndsAt={undoCountdownEndsAt}
                isGuestViewer={isGuestViewer}
                hideResultsBeforeVote={Boolean(isHideResult)}
                allowComments={allowComments}
            />
        </StyledPollItemWrapper>
    );
};
